import React, { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useContextTranslation } from '@core/i18n';
import { isLiveStreamLive } from '@core/livestream/helpers';
import { logMessage } from '@core/logging';
import { isIVS } from '@core/video/videoElementWrapper/helpers';
import UnstyledButton from '@embed/components/common/UnstyledButton';
import { PlayerModal } from '@embed/components/VideoPlayer/Player3/components/PlayerModal';
import { useVideoEl } from '@embed/components/VideoPlayer/Player3/hooks/useVideoEl';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import Modal from '@player/components/Modal';
import { DrawerPosition } from '@player/components/Modal/Drawer';
/**
 * Quality selection modal for IVS streams
 */
export const IVSQualityModal = ({ onDismiss }) => {
    const { t } = useContextTranslation();
    const videoEl = useVideoEl();
    const video = useStore((state) => state.video);
    const ivsQuality = useStore((state) => state.ivsQuality);
    const ivsLowLatencyEnabled = useStore((state) => state.ivsLowLatencyEnabled);
    const { setIvsQuality, setIvsLowLatencyEnabled } = useStore((state) => state.actions);
    const [availableQualities, setAvailableQualities] = useState([]);
    // Get available qualities from IVS player
    useEffect(() => {
        if (!isIVS(videoEl)) {
            return;
        }
        try {
            const qualities = videoEl.getQualities() || [];
            setAvailableQualities(qualities);
        }
        catch (error) {
            setAvailableQualities([]);
        }
    }, [videoEl]);
    const handleQualitySelect = useCallback((quality) => {
        setIvsQuality(quality);
        // Apply the quality setting to the IVS player
        if (isIVS(videoEl)) {
            try {
                if (quality === undefined) {
                    videoEl.setAutoMaxQuality(availableQualities[0]);
                    videoEl.setQuality(availableQualities[0]);
                    videoEl.setAutoQualityMode(true);
                }
                else {
                    videoEl.setQuality(quality);
                }
            }
            catch (error) {
                logMessage('Failed to set IVS quality', {
                    captureContext: {
                        extra: { error },
                    },
                });
            }
        }
    }, [setIvsQuality, videoEl, availableQualities]);
    const handleLowLatencyToggle = useCallback(() => {
        setIvsLowLatencyEnabled(!ivsLowLatencyEnabled);
    }, [setIvsLowLatencyEnabled, ivsLowLatencyEnabled]);
    // Create quality options with auto as default
    const qualityOptions = [
        {
            quality: undefined,
            name: t('player:Auto'),
            isSelected: ivsQuality === undefined,
        },
        ...availableQualities.map((quality) => ({
            quality,
            name: quality.name || `${quality.height}p`,
            isSelected: ivsQuality === quality,
        })),
    ];
    // Check if low latency switch should be shown (IVS livestream that is live)
    const shouldShowLowLatencySwitch = isIVS(videoEl) && isLiveStreamLive(video);
    return (<PlayerModal onBackdropClick={onDismiss} backButton={<Modal.ButtonBack onClick={onDismiss} aria-label={t('player:Back to main menu')}/>} title={t('player:Quality')}>
      <Modal.Drawer color="var(--player-modal-font-color)" backgroundColor="var(--player-modal-background)" position={DrawerPosition.BOTTOM}>
        <div css={css `
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
          `}>
          {qualityOptions.map((option, index) => (<div key={index} css={css `
                border: 1px solid
                  color-mix(in srgb, currentColor 10%, transparent);
                border-radius: var(--radius-3);
                transition: border-color 0.15s ease-in-out;
                ${option.isSelected ? 'border-color: var(--primary);' : ''}

                &:hover {
                  border-color: var(--primary);
                }
              `}>
              <UnstyledButton onClick={() => handleQualitySelect(option.quality)} css={css `
                  padding: 1em;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  width: 100%;
                  text-transform: capitalize;
                `}>
                <span>{option.name}</span>
              </UnstyledButton>
            </div>))}

          {shouldShowLowLatencySwitch && (<div css={css `
                border: 1px solid
                  color-mix(in srgb, currentColor 10%, transparent);
                border-radius: var(--radius-3);
                transition: border-color 0.15s ease-in-out;
                margin-top: var(--spacing-2);

                &:hover {
                  border-color: var(--primary);
                }
              `}>
              <UnstyledButton onClick={handleLowLatencyToggle} css={css `
                  padding: 1em;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  width: 100%;
                `}>
                <span>
                  {ivsLowLatencyEnabled
                ? t('player:Disable Low Latency')
                : t('player:Enable Low Latency')}
                </span>
                <span css={css `
                    font-size: 0.9em;
                    opacity: 0.7;
                  `}>
                  {ivsLowLatencyEnabled ? 'ON' : 'OFF'}
                </span>
              </UnstyledButton>
            </div>)}
        </div>
      </Modal.Drawer>
    </PlayerModal>);
};
